import type { JourneyNote } from '@/api/customer-journey';

/** Date grouping + merge helpers for the journey notes timeline. */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function noteTime(n: JourneyNote): string | undefined {
  // Backend records the note's own timestamp under `customer_journey_notes_time`;
  // fall back to updated/created for older rows.
  return (
    n.customer_journey_notes_time ||
    n.customer_journey_note_updated_at ||
    n.customer_journey_note_created_at
  );
}

/** Author display name — the note carries an embedded `employee` object. */
export function noteAuthor(n: JourneyNote): string {
  return (n.employee?.employee_name || n.employee_name || '').trim() || 'System';
}

/** Local group-date key (no toISOString / UTC drift). */
export function dateKey(iso?: string): string {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function dateLabel(iso?: string): string {
  if (!iso) return 'Undated';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${WEEKDAYS[d.getDay()]} · ${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function timeLabel(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Merge embedded + fetched notes, de-dupe by id (or text+time), newest first. */
export function mergeNotes(embedded: JourneyNote[], fetched: JourneyNote[]): JourneyNote[] {
  const byKey = new Map<string, JourneyNote>();
  for (const n of [...embedded, ...fetched]) {
    const key =
      n.customer_journey_note_id != null
        ? `id:${n.customer_journey_note_id}`
        : `txt:${n.customer_journey_note ?? ''}|${noteTime(n) ?? ''}`;
    if (!byKey.has(key)) byKey.set(key, n);
  }
  return [...byKey.values()].sort((a, b) => {
    const ta = new Date(noteTime(a) ?? 0).getTime();
    const tb = new Date(noteTime(b) ?? 0).getTime();
    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
  });
}

export interface NoteGroup {
  key: string;
  label: string;
  notes: JourneyNote[];
}

export function groupNotesByDate(notes: JourneyNote[]): NoteGroup[] {
  const groups: NoteGroup[] = [];
  const index = new Map<string, NoteGroup>();
  for (const n of notes) {
    const iso = noteTime(n);
    const key = dateKey(iso);
    let group = index.get(key);
    if (!group) {
      group = { key, label: dateLabel(iso), notes: [] };
      index.set(key, group);
      groups.push(group);
    }
    group.notes.push(n);
  }
  return groups;
}
